import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    getSelectedOrder,
    getSelectedOrderId,
} from 'entities/Order/model/selectors/ordersSelectors';
import { convertReadableFormatDate } from 'shared/lib/convertDate/convertDate';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

import { submitHelpRequestService } from '../services/submitHelpRequestService';
import { HelpRequestMessageData } from '../types/submitHelpRequestSchema';

export const useSubmitHelpRequest = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const dispatch = useAppDispatch();
    const orderId = useSelector(getSelectedOrderId);
    const currentOrder = useSelector(getSelectedOrder);

    const submitRequest = async (issueType: string, body: string) => {
        if (!issueType || !orderId || isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            await dispatch(
                submitHelpRequestService({
                    orderId,
                    issueType,
                    body,
                }),
            ).unwrap();

            // Reset form on success
            setIsSubmitted(true);
        } catch {
            toast.error('Form submission failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const messageData: HelpRequestMessageData = useMemo(() => {
        // Success state after submission
        if (isSubmitted) return { type: 'success' };

        // Previous request exists
        if (currentOrder?.helpRequestedBy && currentOrder?.helpRequestedInfo) {
            const { firstName, lastName } = currentOrder.helpRequestedBy;
            const { timestamp, issueType } = currentOrder.helpRequestedInfo;
            const formattedDate = convertReadableFormatDate(timestamp, {
                month: 'long',
            });

            return {
                type: 'post-submission',
                details: {
                    requesterName: `${firstName} ${lastName}`,
                    date: formattedDate,
                    issueType,
                },
            };
        }

        return null;
    }, [
        currentOrder?.helpRequestedBy,
        currentOrder?.helpRequestedInfo,
        isSubmitted,
    ]);

    return { messageData, submitRequest, isSubmitting };
};
